<script setup lang="ts">
import type { ListingFilter } from '~/types/shopware'

interface LanugageOption {
  value: string
  label: string
}
const props = defineProps<{
  filter: ListingFilter
  currentFilters: {
    properties: string[]
  }
}>()

const emits = defineEmits<{
  (e: 'select-value', value: { code: string; value: string[] }): void
}>()

const { t } = useI18n()

// includes other "properties" than language
const properties = computed(() => props.currentFilters?.properties || [])
const languages: ComputedRef<LanugageOption[]> = computed(() => {
  return [...(props.filter.options || []), ...(props.filter.entities || [])].map((option) => ({
    value: option.id,
    label: option.translated?.name || option.name || '',
  }))
})
const languageIds = computed(() => options.value.map((option) => option.value))
const options: ComputedRef<LanugageOption[]> = computed(() => {
  return [
    {
      value: 'all',
      label: t('shop.listingPage.filters.language.all'),
    },
    ...languages.value,
  ]
})
// Safe initialization - this computed will be consistent on server and client
const selectedOption = computed({
  get: () => {
    return properties.value.find((property) => languageIds.value.includes(property)) || 'all'
  },
  set: (value: string) => {
    changeLanguage(value)
  },
})

const changeLanguage = (value: string) => {
  // remove any language id from the current selection
  const selection = properties.value.filter((property) => !languageIds.value.includes(property))

  if (value !== 'all') {
    selection.push(value)
  }

  emits('select-value', {
    code: props.filter.code || '',
    value: selection,
  })
}
</script>

<template>
  <div class="filter-select">
    <Select
      v-model="selectedOption"
      :name="`filter-${filter.name}`"
      :required="false"
      :label="filter.label"
      :options="options"
    />
  </div>
</template>
